import { useAuth0 } from '@auth0/auth0-react'
import { Alert } from '@designsforhealth/dfh-react-components'
import { useLocation } from '@gatsbyjs/reach-router'
import { CircularProgress } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import PrevIcon from '@material-ui/icons/NavigateBefore'
import { tryCatch } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import {
  PostCustomerSupportRequestDocument,
  PostSupportRequestDocument,
  SupportRequestNewInput,
} from '../../../graphql/camel'
import useCamelMutation from '../../../hooks/useCamelMutation'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { useStorage } from '../../../hooks/useStorage'
import useCustomerDisplayName from '../../../lib/auth/hooks/useCustomerDisplayName'
import { SUPPORT_MODAL_HASH } from '../../../lib/support/modal/state'
import ContactSupportMessage from '../../ContactSupportMessage'
import { EmailPattern } from '../../utils/regex'
import SupportSelect from '../SupportSelect'
import TicketField from '../TicketField'
import { TicketFieldsError } from '../TicketField/styled'
import * as Styled from './styled'
import { FormValues, FormValuesRuntype } from './types'
import { formattedTicketFormValues, requiredFieldsErrorMessage } from './utils'

import checkmarkCircleIcon from '../../../img/checkmark-circle.svg'
import logoImage from '../../../img/logo-dark.svg'

const ticketTypes = [
  'Contact Customer Experience about a current or past order',
  'Website Support including consumer shopping, dispensaries and more',
  'Product Questions including questions about formulation, use and others',
  'Designs for Sport',
  'Private Label products',
  'Dietary Supplement Course (DSS)',
]

interface TicketFormProps {
  isOpen: boolean
  handleClose: () => void
  className?: string
}

export const TicketForm: React.VFC<TicketFormProps> = ({ isOpen, handleClose, className }) => {
  const { user } = useAuth0()
  const { pathname, search } = useLocation()
  const { featureFlags } = useSiteMetadata() ?? {}
  const customerDisplayName = useCustomerDisplayName()
  const [ticketType, setTicketType] = useState<number | null>()
  const [formValuesAsString, setFormValuesAsString, clearFormValues] = useStorage({
    key: 'ticket-form',
    defaultValue: JSON.stringify({}),
  })

  const redirectPath = `${pathname}${search}${SUPPORT_MODAL_HASH}`

  const [
    postCustomerSupportRequest,
    {
      error: customerSupportRequestError,
      loading: customerSupportRequestLoading,
      data: customerSupportRequestData,
    },
  ] = useCamelMutation(PostCustomerSupportRequestDocument)
  const [
    postSupportRequest,
    {
      error: anonSupportRequestError,
      loading: anonSupportRequestLoading,
      data: anonSupportRequestData,
    },
  ] = useCamelMutation(PostSupportRequestDocument, { noAuth: true })

  const isSupportRequestLoading = customerSupportRequestLoading || anonSupportRequestLoading
  const supportRequestError = customerSupportRequestError || anonSupportRequestError
  useLogAndCaptureError(supportRequestError)

  const supportRequestCreated = !!(
    customerSupportRequestData?.postCustomerSupportRequest ??
    anonSupportRequestData?.postSupportRequest
  )?.requestId

  const defaultFormValues: FormValues = useMemo(() => {
    const initialValue: FormValues = {
      name: customerDisplayName ? customerDisplayName : '',
      email: user?.email ? user.email : '',
      subject: '',
      details: '',
    }
    if (!formValuesAsString || formValuesAsString === '{}') {
      return initialValue
    }
    return tryCatch(
      () => {
        const formValues = JSON.parse(formValuesAsString)
        if (FormValuesRuntype.guard(formValues)) {
          return formValues
        }
        throw new Error('Ticket form values are not valid')
      },
      (error) => {
        clearFormValues()
        console.log(error)
        return initialValue
      },
    )()
  }, [clearFormValues, customerDisplayName, formValuesAsString, user?.email])

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: defaultFormValues,
  })
  const { formState, handleSubmit, control, register, errors, reset, watch } = methods

  // Populates latest default form values when the form is opened
  useEffect(() => {
    if (isOpen) {
      reset(defaultFormValues)
    }
  }, [defaultFormValues, isOpen, reset])

  const setStoredFormValues = useCallback(() => {
    const { name, email, subject, details } = watch()
    if (!name && !email && !subject && !details && formValuesAsString) {
      clearFormValues()
      return
    }
    setFormValuesAsString(
      JSON.stringify({
        name,
        email,
        subject,
        details,
      }),
    )
  }, [clearFormValues, formValuesAsString, setFormValuesAsString, watch])

  const requiredFieldsError = useMemo(() => requiredFieldsErrorMessage(errors), [errors])
  const { nameError, emailError, subjectError, detailsError } = useMemo(() => {
    const { name, email, subject, details } = errors
    return {
      nameError: name && name.type !== 'required' ? name.message : '',
      emailError: email && email.type !== 'required' ? email.message : '',
      subjectError: subject && subject.type !== 'required' ? subject.message : '',
      detailsError: details && details.type !== 'required' ? details.message : '',
    }
  }, [errors])

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async (formValues: FormValues) => {
      const supportRequestNewInput: SupportRequestNewInput = {
        ...formattedTicketFormValues(formValues),
        type: ticketTypes[ticketType ?? 0],
      }
      const result = await Promise.resolve(
        (user ? postCustomerSupportRequest : postSupportRequest)({
          variables: { supportRequestNewInput },
        }),
      )
      if (!result.errors) {
        clearFormValues()
        reset()
      }
    },
    [ticketType, user, postCustomerSupportRequest, postSupportRequest, clearFormValues, reset],
  )

  const onFormClose = () => {
    // retain form values when closing the modal except after request is created
    if (!supportRequestCreated) {
      setStoredFormValues()
    }
    handleClose()
  }

  if (featureFlags?.anonymousSupportRequest === false && !user) {
    return (
      <Styled.Modal
        aria-describedby="scroll-dialog-description"
        aria-labelledby="scroll-dialog-title"
        className={className}
        onClose={handleClose}
        open={isOpen}
        maxWidth={false}
      >
        <Styled.ModalHeader>
          <Styled.ModalLogo src={logoImage} />
          <Styled.ModalCloseIcon aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </Styled.ModalCloseIcon>
        </Styled.ModalHeader>
        <Styled.ModalContent>
          <Styled.ModalStartTitle>Open a support request</Styled.ModalStartTitle>
          <Styled.ModalMessage>
            <>
              <Styled.LoginButtonLink redirectPath={redirectPath}>Log in</Styled.LoginButtonLink> or{' '}
              <Styled.RegisterButtonLink>create an account</Styled.RegisterButtonLink> to submit a
              support request.
            </>
          </Styled.ModalMessage>
        </Styled.ModalContent>
      </Styled.Modal>
    )
  }

  return (
    <Styled.Modal
      aria-describedby="scroll-dialog-description"
      aria-labelledby="scroll-dialog-title"
      className={className}
      onClose={onFormClose}
      open={isOpen}
      maxWidth={false}
      // prevent scrolling to top when modal is closed
      disableRestoreFocus
    >
      <Styled.ModalHeader>
        <Styled.ModalBackIcon
          aria-label="close"
          hidden={ticketType === null || ticketType === undefined}
          onClick={() => {
            // retain form values when going back
            setStoredFormValues()
            setTicketType(null)
          }}
        >
          <PrevIcon />
        </Styled.ModalBackIcon>
        <Styled.ModalLogo src={logoImage} />
        <Styled.ModalCloseIcon aria-label="close" onClick={onFormClose}>
          <CloseIcon />
        </Styled.ModalCloseIcon>
      </Styled.ModalHeader>
      {!supportRequestCreated ? (
        <Styled.ModalContent>
          <Styled.ModalStartTitle>Open a support request</Styled.ModalStartTitle>
          {(ticketType === null || ticketType === undefined) && (
            <Styled.ModalMessage>
              Like you, thousands of practitioners and consumers trust our products for their health
              goals. We strive for getting those to you on time.
            </Styled.ModalMessage>
          )}
          <Styled.ModalMessage>How can we help you today?</Styled.ModalMessage>
          {ticketType === null || ticketType === undefined ? (
            <Styled.SelectGroup>
              {ticketTypes.map((label, i) => (
                <SupportSelect
                  key={i}
                  label={label}
                  onSelect={() => {
                    setTicketType(i)
                  }}
                />
              ))}
            </Styled.SelectGroup>
          ) : (
            <FormProvider {...methods}>
              <Styled.TicketSubmission onSubmit={handleSubmit(onSubmit)}>
                <SupportSelect label={ticketTypes[ticketType]} isChecked={true} />
                <TicketField
                  name="name"
                  label="Your Full Name"
                  errors={errors}
                  register={() =>
                    register({
                      required: true,
                      // Prevents only whitespace except empty strings
                      validate: (value) =>
                        (typeof value === 'string' && !/^\s+$/.test(value)) ||
                        'Please enter your name',
                      maxLength: {
                        value: 302,
                        message: 'Name field cannot exceed 302 characters',
                      },
                    })
                  }
                />
                {nameError && <TicketFieldsError>{nameError}</TicketFieldsError>}
                <TicketField
                  name="email"
                  label="Your Email"
                  errors={errors}
                  register={() =>
                    register({
                      required: true,
                      pattern: {
                        value: EmailPattern,
                        message: 'Invalid email format',
                      },
                      maxLength: {
                        value: 254,
                        message: 'Email cannot exceed 254 characters',
                      },
                    })
                  }
                />
                {emailError && <TicketFieldsError>{emailError}</TicketFieldsError>}
                <TicketField
                  name="subject"
                  label="What's the subject of this request?"
                  errors={errors}
                  register={() =>
                    register({
                      required: true,
                      // Prevents only whitespace except empty strings
                      validate: (value) =>
                        (typeof value === 'string' && !/^\s+$/.test(value)) ||
                        'Please enter a subject',
                      maxLength: {
                        value: 150,
                        message: 'Subject cannot exceed 150 characters',
                      },
                    })
                  }
                />
                {subjectError && <TicketFieldsError>{subjectError}</TicketFieldsError>}
                <TicketField
                  name="details"
                  label="Please add more details"
                  errors={errors}
                  control={control}
                  rules={{
                    required: true,
                    // Prevents only whitespace except empty strings
                    validate: (value) =>
                      (typeof value === 'string' && !/^\s+$/.test(value)) || 'Please enter details',
                    maxLength: {
                      value: 65535,
                      message: 'Details cannot exceed 65,535 characters',
                    },
                  }}
                  placeholder="Enter a description including the order number or date when applicable"
                  multiline={true}
                />
                {detailsError && <TicketFieldsError>{detailsError}</TicketFieldsError>}
                {requiredFieldsError && (
                  <TicketFieldsError>{requiredFieldsError}</TicketFieldsError>
                )}
                {supportRequestError && (
                  <Alert type="error">
                    An error occurred submitting your request, and we have been notified.{' '}
                    <ContactSupportMessage />
                  </Alert>
                )}
                <Styled.TicketSubmitButton
                  type="submit"
                  disabled={formState.isSubmitting || isSupportRequestLoading}
                >
                  {isSupportRequestLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Create Support Request'
                  )}
                </Styled.TicketSubmitButton>
              </Styled.TicketSubmission>
            </FormProvider>
          )}
        </Styled.ModalContent>
      ) : (
        <Styled.TicketCreated>
          <Styled.IconWrapper>
            <Styled.CheckmarkCircleIcon src={checkmarkCircleIcon} />
          </Styled.IconWrapper>
          <Styled.ModalFinishTitle>Request created!</Styled.ModalFinishTitle>
          <Styled.ModalMessage>
            Thanks for contacting us. <br />
            We&apos;ll be in touch soon.
          </Styled.ModalMessage>
          <Styled.ModalMessage>
            We&apos;ve sent a copy of this request to your inbox.
          </Styled.ModalMessage>
          <Styled.FinishTicketButton onClick={handleClose}>Finish</Styled.FinishTicketButton>
        </Styled.TicketCreated>
      )}
    </Styled.Modal>
  )
}
